import classNames from 'classnames'
import { forwardRef } from 'react'
import Select, { components } from 'react-select'
import { normalizeForFilter } from 'helpers/prepares'
import Icon from 'ui/components/Icon'
import Scrollbar from 'ui/components/Scrollbar'
import { Transition } from 'utils/constants'
import { OptionText, SelectWrap, SelectLabel, SelectError } from './styled'

const CLASSNAME_PREFIX = 'Select'
const BOTTOM_LINE_THEME = 'bottom-line'

const customStyles = {
  option: (styles, state) => {
    return {
      ...styles,
      cursor: 'pointer',
      backgroundColor: state.isFocused
        ? 'var(--t-select-option-hover-bg)'
        : 'transparent',
      fontWeight: 500,
      fontSize: '14px',
      lineHeight: '17px',
      paddingLeft: '16px',
      color: 'var(--t-text-input)',
      outline: 'none !important',

      '&:active': {
        backgroundColor: 'var(--t-select-option-active-bg)',
      },
    }
  },
  control: (styles, state) => {
    const isBottomLineTheme =
      state.selectProps.selectTheme === BOTTOM_LINE_THEME

    if (isBottomLineTheme) {
      return {
        ...styles,
        cursor: 'pointer',
        backgroundColor: 'transparent',
        transition: `all ${Transition.ms300}`,
        minHeight: 'unset',
        height: 'auto',
        border: 'none',
        borderBottom: '2px solid #EEEEEE',
        borderRadius: 0,
        boxShadow: 'none !important',
        outline: 'none !important',
        padding: '0 0 10px',

        '&:hover': {
          border: 'none',
          borderBottom: '2px solid #EEEEEE',
        },
      }
    }

    const borderColor = state.isDisabled
      ? 'var(--t-input-border-disabled)'
      : state.isFocused
        ? 'var(--t-action-accent)'
        : 'var(--t-surface-2)'

    return {
      ...styles,
      height: '42px',
      minHeight: '42px',
      cursor: state.isDisabled ? 'not-allowed' : 'pointer',
      backgroundColor: state.isDisabled
        ? 'var(--t-input-bg-disabled)'
        : 'var(--t-surface-2)',
      borderColor,
      transition: `all ${Transition.ms300}`,
      outline: 'none !important',
      boxShadow: 'none !important',
      borderRadius: '8px',
      opacity: 1,

      '&:hover': {
        borderColor,
      },
    }
  },
  placeholder: (styles) => ({
    ...styles,
    color: 'var(--t-text-secondary)',
    fontWeight: 400,
    margin: 0,
  }),
  indicatorSeparator: (styles) => ({
    ...styles,
    display: 'none',
  }),
  clearIndicator: (styles) => ({
    ...styles,
    padding: '0px',
  }),
  valueContainer: (styles, state) => {
    const isBottomLineTheme =
      state.selectProps.selectTheme === BOTTOM_LINE_THEME

    return {
      ...styles,
      fontWeight: 500,
      fontSize: '14px',
      lineHeight: '17px',
      paddingLeft: isBottomLineTheme ? 0 : '16px',
      paddingRight: 0,
      paddingTop: 0,
      paddingBottom: 0,
    }
  },
  menu: (styles) => ({
    ...styles,
    marginTop: '8px',
    boxShadow:
      '0px 2px 2px -1px rgba(27, 35, 44, 0.04), 0px 8px 16px -2px rgba(27, 36, 44, 0.12)',
    border: '1px solid var(--t-select-popover-border)',
    borderRadius: '6px',
    backgroundColor: 'var(--t-select-popover-bg)',
    zIndex: 10,
  }),
  input: (styles) => ({
    ...styles,
    color: 'var(--t-text-input)',
    margin: 0,
    paddingTop: 0,
    paddingBottom: 0,
  }),
  dropdownIndicator: (styles, state) => {
    const isBottomLineTheme =
      state.selectProps.selectTheme === BOTTOM_LINE_THEME

    const color = state.isDisabled
      ? 'var(--t-input-text-disabled)'
      : 'var(--t-text-input)'

    return {
      ...styles,
      padding: isBottomLineTheme ? 0 : '0 10px 0 0',
      color,
    }
  },
  singleValue: (styles, state) => ({
    ...styles,
    color: state.isDisabled
      ? 'var(--t-input-text-disabled)'
      : 'var(--t-select-multivalue-text)',
    margin: 0,
  }),
}

const DropdownIndicator = (props) => {
  return (
    <components.DropdownIndicator {...props}>
      <Icon name="down" size="24" />
    </components.DropdownIndicator>
  )
}

const Menu = ({ children, selectProps: { maxHeight }, ...props }) => {
  return (
    <components.Menu {...props}>
      <Scrollbar maxHeight={maxHeight}>{children}</Scrollbar>
    </components.Menu>
  )
}

const Option = ({ value, ...props }) => {
  return (
    <components.Option {...props}>
      <OptionText title={value}>{value}</OptionText>
    </components.Option>
  )
}

const CommonSelect = forwardRef(
  (
    {
      label,
      meta,
      error,
      required,
      type,
      className,
      noMargin,
      selectTheme,
      ...props
    },
    ref
  ) => {
    const isTouched = meta && meta.touched
    const hasError = isTouched && meta.error

    return (
      <SelectWrap
        $noMargin={noMargin}
        $selectTheme={selectTheme}
        ref={ref}
        type={type}
        className={classNames(
          {
            'has-label': label,
            'has-error': hasError,
          },
          className
        )}
      >
        {label && (
          <SelectLabel type={type}>
            {label} {required && <span>*</span>}
          </SelectLabel>
        )}
        <Select
          isClearable={false}
          styles={customStyles}
          captureMenuScroll={false}
          classNamePrefix={CLASSNAME_PREFIX}
          noOptionsMessage={() => 'Не найдено'}
          components={{
            Option,
            DropdownIndicator,
            Menu,
          }}
          placeholder="Не выбран"
          selectTheme={selectTheme}
          filterOption={(option, inputValue) =>
            normalizeForFilter(option.value, inputValue) ? option.data : ''
          }
          {...props}
        />
        {hasError && <SelectError>{error || meta.error}</SelectError>}
      </SelectWrap>
    )
  }
)

export default CommonSelect

CommonSelect.displayName = 'CommonSelect'
